import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  Plus,
  RefreshCw,
  Users,
  UserCheck,
  UserX,
  Store,
  Pencil,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Filter,
  X,
  Handshake,
  LoaderCircle,
  CalendarDays,
  Phone,
  Mail,
  Hash,
  MoreHorizontal,
  Power,
  Trash2,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

import ConfirmDialog from "@/components/common/ConfirmDialog";

const PAGE_SIZES = [10, 25, 50, 100];

const inputClass =
  "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10";

const buttonClass =
  "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getInitials = (name) =>
  (name || "SP")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

const getErrorMessage = (error) =>
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong. Please try again.";

const StatCard = ({ title, value, description, icon: Icon }) => (
  <div className="rounded-2xl border border-border bg-card p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="mt-2 text-2xl font-bold tracking-tight">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="rounded-xl bg-primary/10 p-3 text-primary">
        <Icon size={21} />
      </div>
    </div>
  </div>
);

const StatusBadge = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
      active
        ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
        : "bg-muted text-muted-foreground"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        active ? "bg-emerald-500" : "bg-muted-foreground"
      }`}
    />
    {active ? "Active" : "Inactive"}
  </span>
);

const IconButton = ({ title, onClick, disabled, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={onClick}
    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border transition hover:bg-muted disabled:opacity-50"
  >
    {children}
  </button>
);

const PartnerDialog = ({ open, partner, saving, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    active: true,
  });

  useEffect(() => {
    if (!open) return;

    setForm({
      name: partner?.name || "",
      email: partner?.email || "",
      phone: partner?.phone || "",
      active: partner?.active !== false,
    });
  }, [open, partner]);

  if (!open) return null;

  const editing = Boolean(partner);

  const handleSubmit = (event) => {
    event.preventDefault();

    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    const phone = form.phone.trim();

    if (!name) {
      toast.error("Please enter the partner name.");
      return;
    }

    if (!editing && !email) {
      toast.error("Please enter the partner email.");
      return;
    }

    if (!editing && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    onSave({
      name,
      email,
      phone,
      active: form.active,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="partner-dialog-title"
        className="my-auto w-full max-w-lg rounded-2xl border border-border bg-background shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-border p-5">
          <div>
            <h2 id="partner-dialog-title" className="text-lg font-bold">
              {editing ? "Edit Sales Partner" : "Add Sales Partner"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {editing
                ? "Update partner details and account status."
                : "Create a partner with a unique referral code."}
            </p>
          </div>
          <IconButton title="Close dialog" disabled={saving} onClick={onClose}>
            <X size={18} />
          </IconButton>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div>
            <label className="mb-1.5 block text-sm font-medium">
              Full Name <span className="text-destructive">*</span>
            </label>
            <input
              autoFocus
              required
              maxLength={100}
              className={inputClass}
              placeholder="Enter partner name"
              value={form.name}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, name: event.target.value }))
              }
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium">
              Email Address{" "}
              {!editing && <span className="text-destructive">*</span>}
            </label>
            <input
              required={!editing}
              type="email"
              maxLength={254}
              disabled={editing}
              className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
              placeholder="partner@example.com"
              value={form.email}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, email: event.target.value }))
              }
            />
            {editing && (
              <p className="mt-1 text-xs text-muted-foreground">
                Email cannot be changed through the current API.
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium">
              Phone Number
            </label>
            <input
              type="tel"
              maxLength={20}
              className={inputClass}
              placeholder="Enter phone number"
              value={form.phone}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, phone: event.target.value }))
              }
            />
          </div>

          {editing && (
            <label className="flex cursor-pointer items-center justify-between rounded-xl border border-border p-3">
              <div>
                <span className="block text-sm font-medium">
                  Partner status
                </span>
                <span className="text-xs text-muted-foreground">
                  Inactive partners cannot use their referral code.
                </span>
              </div>
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={form.active}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    active: event.target.checked,
                  }))
                }
              />
            </label>
          )}

          <div className="flex justify-end gap-3 border-t border-border pt-4">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className={`${buttonClass} border border-border hover:bg-muted`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`${buttonClass} bg-primary text-primary-foreground hover:opacity-90`}
            >
              {saving && <LoaderCircle size={16} className="animate-spin" />}
              {saving
                ? "Saving..."
                : editing
                  ? "Save Changes"
                  : "Create Partner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function SalesPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [copiedCode, setCopiedCode] = useState("");
  const [actionPartner, setActionPartner] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDeletePartner, setSelectedDeletePartner] = useState(null);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatusPartner, setSelectedStatusPartner] = useState(null);

  const [restaurantsOpen, setRestaurantsOpen] = useState(false);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);
  const [selectedPartnerRestaurants, setSelectedPartnerRestaurants] =
    useState(null);

  const loadPartners = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const response = await api.adminSalesPartners();
      const data = response?.data ?? response;
      const rows = Array.isArray(data)
        ? data
        : (data?.sales_partners ?? data?.partners ?? []);

      setPartners(rows);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const stats = useMemo(() => {
    const active = partners.filter((partner) => partner.active !== false);
    const inactive = partners.filter((partner) => partner.active === false);
    const restaurants = partners.reduce(
      (total, partner) => total + (Number(partner.restaurant_count) || 0),
      0,
    );

    return {
      total: partners.length,
      active: active.length,
      inactive: inactive.length,
      restaurants,
    };
  }, [partners]);

  const filteredPartners = useMemo(() => {
    const term = search.trim().toLowerCase();

    return partners.filter((partner) => {
      const matchesSearch =
        !term ||
        [
          partner.name,
          partner.email,
          partner.phone,
          partner.referral_code,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(term),
        );

      const active = partner.active !== false;

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && active) ||
        (statusFilter === "inactive" && !active);

      const created = partner.created_at ? new Date(partner.created_at) : null;

      const validCreated = created && !Number.isNaN(created.getTime());

      const matchesFrom =
        !fromDate ||
        (validCreated && created >= new Date(`${fromDate}T00:00:00`));

      const matchesTo =
        !toDate ||
        (validCreated && created <= new Date(`${toDate}T23:59:59.999`));

      return matchesSearch && matchesStatus && matchesFrom && matchesTo;
    });
  }, [partners, search, statusFilter, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredPartners.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pagePartners = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPartners.slice(start, start + pageSize);
  }, [filteredPartners, currentPage, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, fromDate, toDate, pageSize]);

  const openCreate = () => {
    setEditingPartner(null);
    setDialogOpen(true);
  };

  const openEdit = (partner) => {
    setEditingPartner(partner);
    setDialogOpen(true);
  };

  const handleSave = async (form) => {
    setSaving(true);

    try {
      if (editingPartner) {
        await api.updateAdminSalesPartner(editingPartner.id, {
          name: form.name,
          phone: form.phone,
          active: form.active,
        });
        toast.success("Sales partner updated successfully.");
      } else {
        await api.createAdminSalesPartner({
          name: form.name,
          email: form.email,
          phone: form.phone,
        });
        toast.success("Sales partner created successfully.");
      }

      setDialogOpen(false);
      setEditingPartner(null);
      await loadPartners(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = (partner) => {
    setSelectedStatusPartner(partner);
    setStatusDialogOpen(true);
  };

  const confirmToggleStatus = async () => {
    if (!selectedStatusPartner) return;

    const partner = selectedStatusPartner;
    const nextActive = partner.active === false;

    setActionPartner(partner.id);

    try {
      await api.updateAdminSalesPartner(partner.id, {
        name: partner.name || "",
        phone: partner.phone || "",
        active: nextActive,
      });

      toast.success(nextActive ? "Partner activated." : "Partner deactivated.");

      setStatusDialogOpen(false);
      setSelectedStatusPartner(null);

      await loadPartners(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionPartner(null);
    }
  };

  const handleDeletePartner = async () => {
    if (!selectedDeletePartner) return;

    const partner = selectedDeletePartner;

    if (partner.active !== false) {
      toast.error("Deactivate the partner before deleting.");
      return;
    }

    setActionPartner(partner.id);

    try {
      await api.deleteAdminSalesPartner(partner.id);

      toast.success("Sales partner deleted successfully.");

      setDeleteDialogOpen(false);
      setSelectedDeletePartner(null);

      await loadPartners(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionPartner(null);
    }
  };

  const handleViewRestaurants = async (partner) => {
    setRestaurantsOpen(true);
    setRestaurantsLoading(true);
    setSelectedPartnerRestaurants(null);

    try {
      const response = await api.adminSalesPartnerRestaurants(
        partner.id || partner._id,
      );
      setSelectedPartnerRestaurants(response?.data ?? response);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setRestaurantsOpen(false);
    } finally {
      setRestaurantsLoading(false);
    }
  };

  const handleCopy = async (code) => {
    if (!code) {
      toast.error("No referral code available.");
      return;
    }

    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success("Referral code copied.");

      window.setTimeout(() => {
        setCopiedCode((current) => (current === code ? "" : current));
      }, 1800);
    } catch {
      toast.error("Unable to copy. Please copy the code manually.");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const hasFilters = search || statusFilter !== "all" || fromDate || toDate;

  return (
    <div className="min-h-full space-y-6 p-4 sm:p-6 lg:p-8">
      <PartnerDialog
        open={dialogOpen}
        partner={editingPartner}
        saving={saving}
        onClose={() => {
          setDialogOpen(false);
          setEditingPartner(null);
        }}
        onSave={handleSave}
      />

      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2">
            <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
              <Handshake size={23} />
            </div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Sales Partners
            </h1>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage sales partners, referral codes and restaurant referrals.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => loadPartners(true)}
            disabled={refreshing || loading}
            className={`${buttonClass} border border-border bg-background hover:bg-muted`}
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            onClick={openCreate}
            className={`${buttonClass} bg-primary text-primary-foreground shadow-sm hover:opacity-90`}
          >
            <Plus size={17} />
            Add Partner
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Total Partners"
          value={stats.total}
          description="All registered sales partners"
          icon={Users}
        />
        <StatCard
          title="Active Partners"
          value={stats.active}
          description="Referral codes enabled"
          icon={UserCheck}
        />
        <StatCard
          title="Inactive Partners"
          value={stats.inactive}
          description="Referral codes disabled"
          icon={UserX}
        />
        <StatCard
          title="Referred Restaurants"
          value={stats.restaurants}
          description="Restaurants attributed to partners"
          icon={Store}
        />
      </div>

      <section className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="border-b border-border p-4 sm:p-5">
          <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
            <div>
              <h2 className="text-lg font-bold">Partner Directory</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Search, filter and manage your sales partner accounts.
              </p>
            </div>
            <span className="w-fit rounded-full bg-muted px-3 py-1.5 text-xs font-semibold">
              {filteredPartners.length} result
              {filteredPartners.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="mt-5 grid grid-cols-1 items-end gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(260px,2fr)_minmax(170px,1fr)_minmax(170px,1fr)_minmax(170px,1fr)]">
            <div className="relative min-w-0">
              <Search
                size={17}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="search"
                aria-label="Search partners"
                className={`${inputClass} h-11 pl-9`}
                placeholder="Search name, email, phone or code..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div className="relative">
              <Filter
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <select
                aria-label="Filter by status"
                className={`${inputClass} h-11 pl-9`}
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="active">Active only</option>
                <option value="inactive">Inactive only</option>
              </select>
            </div>

            <div className="flex min-w-0 flex-col gap-1.5">
              <label className="block text-xs leading-4 text-muted-foreground">
                Joined from
              </label>
              <input
                type="date"
                aria-label="Joined from date"
                className={`${inputClass} h-11 min-w-0`}
                value={fromDate}
                max={toDate || undefined}
                onChange={(event) => setFromDate(event.target.value)}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs text-muted-foreground">
                Joined until
              </label>
              <input
                type="date"
                aria-label="Joined until date"
                className={inputClass}
                value={toDate}
                min={fromDate || undefined}
                onChange={(event) => setToDate(event.target.value)}
              />
            </div>
          </div>

          {hasFilters && (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                <X size={14} />
                Clear filters
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex min-h-64 flex-col items-center justify-center gap-3 p-8 text-muted-foreground">
            <LoaderCircle size={30} className="animate-spin text-primary" />
            <p className="text-sm">Loading sales partners...</p>
          </div>
        ) : partners.length === 0 ? (
          <div className="flex min-h-72 flex-col items-center justify-center px-5 py-12 text-center">
            <div className="rounded-2xl bg-muted p-4">
              <Handshake size={30} className="text-muted-foreground" />
            </div>
            <h3 className="mt-4 text-lg font-bold">No sales partners yet</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Add your first sales partner to start tracking restaurant
              referrals.
            </p>
            <button
              type="button"
              onClick={openCreate}
              className={`${buttonClass} mt-5 bg-primary text-primary-foreground`}
            >
              <Plus size={16} />
              Add First Partner
            </button>
          </div>
        ) : filteredPartners.length === 0 ? (
          <div className="flex min-h-64 flex-col items-center justify-center p-8 text-center">
            <Search size={28} className="text-muted-foreground" />
            <h3 className="mt-3 font-semibold">No matching partners</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Try changing your search or filters.
            </p>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-3 text-sm font-semibold text-primary hover:underline"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1050px] border-collapse text-left text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-5 py-4 font-semibold">Sales Partner</th>
                    <th className="px-5 py-4 font-semibold">Contact</th>
                    <th className="px-5 py-4 font-semibold">Referral Code</th>
                    <th className="px-5 py-4 text-center font-semibold">
                      Restaurants
                    </th>
                    <th className="px-5 py-4 font-semibold">Joined</th>
                    <th className="px-5 py-4 font-semibold">Status</th>
                    <th className="px-5 py-4 text-right font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-border">
                  {pagePartners.map((partner) => (
                    <tr
                      key={partner.id || partner._id}
                      className="transition hover:bg-muted/30"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-bold text-primary">
                            {getInitials(partner.name)}
                          </div>
                          <div className="relative min-w-0">
                            <p className="max-w-52 truncate font-semibold">
                              {partner.name || "Unnamed partner"}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              ID: {partner.id || partner._id || "—"}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="space-y-1.5">
                          <p className="flex items-center gap-2">
                            <Mail
                              size={14}
                              className="shrink-0 text-muted-foreground"
                            />
                            <span className="max-w-48 truncate">
                              {partner.email || "—"}
                            </span>
                          </p>
                          <p className="flex items-center gap-2 text-muted-foreground">
                            <Phone size={14} className="shrink-0" />
                            {partner.phone || "—"}
                          </p>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border bg-muted/40 px-2.5 py-1.5 font-mono text-xs font-bold tracking-wide">
                            <Hash size={12} />
                            {partner.referral_code || "—"}
                          </span>
                          {partner.referral_code && (
                            <IconButton
                              title="Copy referral code"
                              onClick={() => handleCopy(partner.referral_code)}
                            >
                              {copiedCode === partner.referral_code ? (
                                <Check size={15} className="text-emerald-600" />
                              ) : (
                                <Copy size={15} />
                              )}
                            </IconButton>
                          )}
                        </div>
                      </td>

                      <td className="px-5 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="inline-flex min-w-10 items-center justify-center rounded-lg bg-muted px-2.5 py-1.5 font-semibold tabular-nums">
                            {Number(partner.restaurant_count) || 0}
                          </span>

                          <IconButton
                            title="View referred restaurants"
                            onClick={() => handleViewRestaurants(partner)}
                          >
                            <Eye size={15} />
                          </IconButton>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-2 whitespace-nowrap text-muted-foreground">
                          <CalendarDays size={15} />
                          {formatDate(partner.created_at)}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <StatusBadge active={partner.active !== false} />
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <IconButton
                            title="Edit partner"
                            onClick={() => openEdit(partner)}
                          >
                            <Pencil size={15} />
                          </IconButton>

                          <IconButton
                            title={
                              partner.active === false
                                ? "Activate partner"
                                : "Deactivate partner"
                            }
                            disabled={actionPartner === partner.id}
                            onClick={() => handleToggleStatus(partner)}
                          >
                            {actionPartner === partner.id ? (
                              <LoaderCircle
                                size={15}
                                className="animate-spin"
                              />
                            ) : (
                              <Power
                                size={15}
                                className={
                                  partner.active === false
                                    ? "text-emerald-600"
                                    : "text-destructive"
                                }
                              />
                            )}
                          </IconButton>
                          {partner.active === false && (
                            <IconButton
                              title="Delete inactive partner"
                              disabled={actionPartner === partner.id}
                              onClick={() => {
                                setSelectedDeletePartner(partner);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              {actionPartner === partner.id ? (
                                <LoaderCircle
                                  size={15}
                                  className="animate-spin"
                                />
                              ) : (
                                <Trash2
                                  size={15}
                                  className="text-destructive"
                                />
                              )}
                            </IconButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col justify-between gap-4 border-t border-border px-4 py-4 sm:flex-row sm:items-center sm:px-5">
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span>Rows per page</span>
                <select
                  aria-label="Rows per page"
                  className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                  value={pageSize}
                  onChange={(event) => setPageSize(Number(event.target.value))}
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span>
                  Showing{" "}
                  {filteredPartners.length === 0
                    ? 0
                    : (currentPage - 1) * pageSize + 1}
                  {"–"}
                  {Math.min(
                    currentPage * pageSize,
                    filteredPartners.length,
                  )} of {filteredPartners.length}
                </span>
              </div>

              <div className="flex items-center justify-between gap-3 sm:justify-end">
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    aria-label="Previous page"
                    title="Previous page"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border transition hover:bg-muted disabled:opacity-40"
                  >
                    <ChevronLeft size={17} />
                  </button>
                  <button
                    type="button"
                    aria-label="Next page"
                    title="Next page"
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setPage((value) => Math.min(totalPages, value + 1))
                    }
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border transition hover:bg-muted disabled:opacity-40"
                  >
                    <ChevronRight size={17} />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-4">
        <div className="mt-0.5 text-muted-foreground">
          <MoreHorizontal size={18} />
        </div>
        <div>
          <p className="text-sm font-semibold">Partner management note</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Deactivating a partner prevents their referral code from being used
            for new signups. Existing restaurant attribution remains intact.
            Permanent deletion is not provided here because the current API does
            not expose a delete endpoint.
          </p>
        </div>
      </div>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Sales Partner?"
        message={
          selectedDeletePartner
            ? `Are you sure you want to permanently delete ${selectedDeletePartner.name} (${selectedDeletePartner.referral_code})? This action cannot be undone.`
            : "This action cannot be undone."
        }
        confirmText="Delete Partner"
        cancelText="Cancel"
        danger={true}
        loading={Boolean(
          selectedDeletePartner && actionPartner === selectedDeletePartner.id,
        )}
        onCancel={() => {
          if (actionPartner) return;

          setDeleteDialogOpen(false);
          setSelectedDeletePartner(null);
        }}
        onConfirm={handleDeletePartner}
      />

      <ConfirmDialog
        open={statusDialogOpen}
        title={
          selectedStatusPartner?.active === false
            ? "Activate Sales Partner?"
            : "Deactivate Sales Partner?"
        }
        message={
          selectedStatusPartner
            ? selectedStatusPartner.active === false
              ? `Are you sure you want to activate ${selectedStatusPartner.name || "this partner"}? Their referral code will become usable again.`
              : `Are you sure you want to deactivate ${selectedStatusPartner.name || "this partner"}? Their referral code will no longer be accepted for new signups.`
            : ""
        }
        confirmText={
          selectedStatusPartner?.active === false
            ? "Activate Partner"
            : "Deactivate Partner"
        }
        cancelText="Cancel"
        danger={selectedStatusPartner?.active !== false}
        loading={Boolean(
          selectedStatusPartner && actionPartner === selectedStatusPartner.id,
        )}
        onCancel={() => {
          if (actionPartner) return;
          setStatusDialogOpen(false);
          setSelectedStatusPartner(null);
        }}
        onConfirm={confirmToggleStatus}
      />

      {restaurantsOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-4xl rounded-2xl border border-border bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border p-5">
              <div>
                <h2 className="text-lg font-bold">Referred Restaurants</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {selectedPartnerRestaurants?.sales_partner?.name ||
                    "Sales Partner"}
                  {" · "}
                  Total: {selectedPartnerRestaurants?.total ?? 0}
                </p>
              </div>

              <IconButton
                title="Close"
                onClick={() => {
                  setRestaurantsOpen(false);
                  setSelectedPartnerRestaurants(null);
                }}
              >
                <X size={18} />
              </IconButton>
            </div>

            <div className="max-h-[65vh] overflow-auto p-5">
              {restaurantsLoading ? (
                <div className="flex justify-center p-10">
                  <LoaderCircle className="animate-spin" size={28} />
                </div>
              ) : (
                <table className="w-full min-w-[650px] text-left text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-3">Restaurant</th>
                      <th className="p-3">Email</th>
                      <th className="p-3">Phone</th>
                      <th className="p-3">Plan</th>
                      <th className="p-3">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(selectedPartnerRestaurants?.restaurants || []).map(
                      (restaurant) => (
                        <tr key={restaurant.id}>
                          <td className="p-3 font-medium">
                            {restaurant.name || "—"}
                          </td>
                          <td className="p-3">{restaurant.email || "—"}</td>
                          <td className="p-3">{restaurant.phone || "—"}</td>
                          <td className="p-3">{restaurant.plan || "—"}</td>
                          <td className="p-3">
                            {formatDate(restaurant.created_at)}
                          </td>
                        </tr>
                      ),
                    )}

                    {!selectedPartnerRestaurants?.restaurants?.length &&
                      !restaurantsLoading && (
                        <tr>
                          <td
                            colSpan={5}
                            className="p-8 text-center text-muted-foreground"
                          >
                            No referred restaurants found.
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
